import { ITemplateBaseDetails, TEMPLATE_TYPE } from '../../../../lib/types';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { TemplateTable, ITemplateTableRowValues } from './template-table.component';
import { activeStatus } from '../../../../utils/wording.helpers';
import { TemplateActionButton } from './template-action-button';
import { TemplateTableMultiselectActionButton } from './template-table-multiselect-action-button/template-table-multiselect-action-button';
import { IAdminProps, IPersonalCompanyProps } from '../../../_core/core.types';
import {
  ITableFilterConfig,
  ITableSearchConfig,
  ITableSortConfig,
  TableFilterKeys
} from '../../../_core/table/table.types';
import { useTableFilterConfig, useTableSearchConfig, useTableSelect } from '../../../_core/table/utils/table-utils';
import { ITableFilterOption } from '../../../_core/table/table.types';
import { RIDialog } from '../../../_core/dialog/dialog.component';
import { EditRequestContainer } from '../../../domains/request/edit/edit-request.container';
import { PreviewTemplateContainer } from '../../../domains/template/preview/preview-template.container';
import { GlobalIcon } from '../../../_core/icon/global-icon.component';
import { useFetchCurrUser } from '../../../domains/users/user.service';
import { BlockTitles, BlockType } from '../../../_editor/_core/editor.const';
import { SetURLSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import { TableDate } from '../../../_core/table/table-date.component';
import { ROUTE_PATHS } from '../../../../_routes';
import { sortMethodTuple, useTableSortConfig } from '../../../_core/table/utils/table-sort';
import { Button } from '../../../_core/button/button.component';
import { CreateBlankTemplateContainer } from '../../../domains/template/create-blank/create-blank-template-form.container';
import { getName } from '../../../../utils/name.utils';

enum TemplateTableType {
  REQUEST = 'Request',
  FORM = 'Question'
}

interface ITemplateTableNestedContainerProps extends IAdminProps, IPersonalCompanyProps {
  loading: boolean;
  templates?: ITemplateBaseDetails[];
}

export const TemplateTableNestedContainer: React.FC<ITemplateTableNestedContainerProps> = ({
  isPersonalCompany,
  templates,
  ...rest
}) => {
  const navigate = useNavigate();

  const { isGlobalAdmin } = useFetchCurrUser();

  const [previewTemplate, setPreviewTemplate] = useState<ITemplateBaseDetails | null>(null);
  const [requestTemplate, setRequestTemplate] = useState<ITemplateBaseDetails | null>(null);

  // Get templates for table mode
  const [searchParams, setSearchParams] = useSearchParams();
  const [mode, setMode] = useState<string>(
    searchParams.has('mode')
      ? Object.values(TemplateTableType).find((v) => v.toLowerCase() === searchParams.get('mode')?.toLowerCase()) ??
          TemplateTableType.REQUEST
      : TemplateTableType.REQUEST
  );

  const templatesForMode = useMemo(
    () =>
      templates?.filter((t) =>
        mode === TemplateTableType.REQUEST ? t.type === TEMPLATE_TYPE.REQUEST : t.type !== TEMPLATE_TYPE.REQUEST
      ),
    [mode, templates]
  );

  // Filter templates
  const showBlockType = useMemo(() => mode === TemplateTableType.FORM, [mode]);
  const filters = useMemo(() => {
    const newFilters: ITableFilterOption<ITemplateBaseDetails>[] = [];

    if (showBlockType)
      newFilters.push({
        containerClass: 'w-96',
        title: TableFilterKeys.TYPE,
        placeholder: 'Filter by Type',
        options: Object.values(BlockType),
        labeler: (value) => (value in BlockTitles ? BlockTitles[value as BlockType] : undefined),
        value: [],
        matcher: (curr: ITemplateBaseDetails, filterValue: string[]) => {
          return !filterValue.length || (!!curr.blockType && filterValue.includes(curr.blockType));
        }
      });

    return newFilters;
  }, [showBlockType]);

  const [filterConfig, filterFunc] = useTableFilterConfig({ activeStatusFilter: true, filters, ownershipFilter: true });
  const filteredTemplates = useMemo(() => templatesForMode?.filter(filterFunc), [templatesForMode, filterFunc]);

  // Filter template by search text
  const [searchConfig, searchMatches] = useTableSearchConfig({
    items: filteredTemplates,
    keys: ['description', 'title']
  });

  //
  const { sortFunc, ...restSort } = useTableSortConfig({
    sortMethods: {
      title: sortMethodTuple(),
      blockType: sortMethodTuple(),
      type: sortMethodTuple(),
      updatedAt: sortMethodTuple('date')
    },
    defaultSortFunc: (a: ITemplateBaseDetails, b) => {
      // Sort by status, block type, title, and then date desc
      if (a.deletedAt && !b.deletedAt) return 1;
      if (!a.deletedAt && b.deletedAt) return -1;

      const blockCompare = a.blockType?.localeCompare(b.blockType ?? '');
      if (blockCompare) return blockCompare;

      const titleCompare = a.title.localeCompare(b.title);
      if (titleCompare) return titleCompare;

      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    }
  });

  // Transform sorted models in table rows, and sort filtered templates
  const [sorted, templateRows] = useMemo(() => {
    // NOTE: Having sorted as it's own memo would cause the row transformation not to trigger on sorting updates.
    //       I'm not sure why, so I've merged them into a single memo that will handle both updates.
    const newSorted = searchMatches?.sort(sortFunc);
    return [
      newSorted,
      newSorted?.map((template, tIndex) => {
        const row: ITemplateTableRowValues = {
          rowId: template._id,
          template,
          values: [
            {
              children: (
                <div className="flex">
                  {!template.company && <GlobalIcon text="Global Template" />}
                  <span>{template.title}</span>
                </div>
              )
            },
            { children: <TableDate date={template.updatedAt} showTime /> },
            { children: template.createdBy ? getName(template.createdBy) : '-' },
            { children: activeStatus(!template.deletedAt) },
            { children: template.type },
            { children: template.description }
          ]
        };

        if (showBlockType)
          row.values.push({ children: template.blockType ? BlockTitles[template.blockType as BlockType] : null });

        row.values.push({
          children: (
            <TemplateActionButton
              index={tIndex}
              template={template}
              isGlobalAdmin={isGlobalAdmin}
              isPersonalCompany={isPersonalCompany}
              onCreateRequest={mode === TemplateTableType.REQUEST ? () => setRequestTemplate(template) : undefined}
              onEdit={() =>
                navigate(ROUTE_PATHS.TEMPLATE + '/' + template._id, {
                  state: { backPath: window.location.pathname + window.location.search }
                })
              }
              onPreview={() => setPreviewTemplate(template)}
            />
          )
        });

        return row;
      })
    ];
  }, [searchMatches, sortFunc, showBlockType, isGlobalAdmin, isPersonalCompany, mode, navigate]);

  const handleRowDoubleClick = useCallback(
    (index: number) => {
      const template = sorted?.[index];
      if (!template) {
        console.warn('handleRowDoubleClick: template not found at index', index);
        return;
      }
      navigate(ROUTE_PATHS.TEMPLATE + '/' + template._id, {
        state: { backPath: window.location.pathname + window.location.search }
      });
    },
    [sorted, navigate]
  );

  return (
    <>
      <TemplateTableNestedSelectContainer
        {...rest}
        {...{ filterConfig, mode, searchConfig, setMode, setSearchParams, showBlockType }}
        models={sorted}
        onRowDoubleClick={handleRowDoubleClick}
        rows={templateRows}
        restSortConfig={restSort}
      />
      {!!requestTemplate && (
        <RIDialog open={!!requestTemplate} setOpen={(o) => setRequestTemplate((v) => (o ? v : null))}>
          <EditRequestContainer template={requestTemplate} />
        </RIDialog>
      )}
      {!!previewTemplate && (
        <RIDialog open={!!previewTemplate} setOpen={(o) => setPreviewTemplate((v) => (o ? v : null))}>
          <PreviewTemplateContainer templateId={previewTemplate._id} />
        </RIDialog>
      )}
    </>
  );
};

// NOTE: Needed to separate this final container so that select config doesn't lead to infinite rendering updates. Not entirely sure why that is happening
const TemplateTableNestedSelectContainer: React.FC<
  ITemplateTableNestedContainerProps & {
    filterConfig: ITableFilterConfig<ITemplateBaseDetails>;
    mode: string;
    models?: ITemplateBaseDetails[];
    onRowDoubleClick: (index: number) => void;
    restSortConfig: Omit<ITableSortConfig<ITemplateBaseDetails>, 'sortFunc'>;
    rows?: ITemplateTableRowValues[];
    searchConfig: ITableSearchConfig;
    setMode: Dispatch<SetStateAction<string>>;
    setSearchParams: SetURLSearchParams;
    showBlockType: boolean;
  }
> = ({
  filterConfig,
  mode,
  models,
  onRowDoubleClick,
  rows,
  loading,
  isAdmin,
  isPersonalCompany,
  restSortConfig,
  searchConfig,
  setMode,
  setSearchParams,
  showBlockType
}) => {
  const [creating, setCreating] = useState(false);

  const { clearSelected, getSelectedModels, onSelect, onSelectAll, selected } = useTableSelect({ models, rows });

  return (
    <>
      <TemplateTable
        {...restSortConfig}
        isAdmin={isAdmin}
        isPersonalCompany={isPersonalCompany}
        selected={selected}
        templates={rows ?? []}
        onSelect={onSelect}
        onSelectAll={onSelectAll}
        onRowDoubleClick={onRowDoubleClick}
        filterConfig={filterConfig}
        modeConfig={{
          onChange: (v) => {
            setMode(v);
            setSearchParams({ mode: v.toLowerCase() });
          },
          options: Object.values(TemplateTableType),
          value: mode
        }}
        searchConfig={searchConfig}
        showBlockType={showBlockType}
        loading={loading}
      >
        <div className="flex items-center w-full max-w-96 justify-end gap-2 mt-1">
          <Button onClick={() => setCreating(true)} size="large">
            Create Template
          </Button>
          <TemplateTableMultiselectActionButton
            getSelectedModels={getSelectedModels}
            selected={selected ?? []}
            clearSelected={clearSelected}
          />
        </div>
      </TemplateTable>
      <RIDialog open={creating} setOpen={setCreating}>
        <CreateBlankTemplateContainer />
      </RIDialog>
    </>
  );
};
